import { useCallback, useEffect, useMemo, useState } from 'react'
import * as api from '../lib/api'
import Select from './Select'
import { SkeletonRow } from './Skeleton'
import { dateText, whenText } from '../lib/when'
import { askRange, monthRange } from '../lib/period'
import SampleTag from './SampleTag'
import ClientLedgers from './ClientLedgers'
import { isSampleCode } from '../lib/samples'

/**
 * Every client's reconciliation, one row per collection cycle — the
 * Reconciler page's main view. The backend decides each row's `stage`
 * (ledgerReconcile.service.js#reconciliationTracker); this only filters,
 * counts and offers the one action that stage allows.
 */

export const STAGES = {
  collecting: { label: 'Waiting on collection', tone: 'p-mute' },
  ready: { label: 'Ready to reconcile', tone: 'p-info' },
  reconciling: { label: 'Reconciling', tone: 'p-info' },
  needs_client_fix: { label: 'Client to fix', tone: 'p-warn' },
  needs_our_fix: { label: 'We need to fix', tone: 'p-warn' },
  needs_both_fix: { label: 'Both sides to fix', tone: 'p-bad' },
  fixes_back: { label: 'Fixes in — re-checking', tone: 'p-info' },
  clean: { label: 'Reconciled — clean', tone: 'p-ok' },
  failed: { label: 'Run failed', tone: 'p-bad' },
}

const NEEDS_FIX = new Set(['needs_client_fix', 'needs_our_fix', 'needs_both_fix'])

/** What each summary tile counts, and what clicking it filters to. */
const TILES = [
  { key: 'collecting', label: 'Waiting on collection', accent: 'var(--ink-mute)', match: (s) => s === 'collecting' },
  { key: 'ready', label: 'Ready to reconcile', accent: 'var(--blue)', match: (s) => s === 'ready' },
  { key: 'reconciling', label: 'Reconciling now', accent: 'var(--gold)', match: (s) => s === 'reconciling' || s === 'fixes_back' },
  { key: 'fix', label: 'Needs a fix', accent: 'var(--amber)', match: (s) => NEEDS_FIX.has(s) },
  { key: 'clean', label: 'Reconciled clean', accent: 'var(--green)', match: (s) => s === 'clean' },
]

const STAGE_FILTERS = [
  { value: 'all', label: 'Any stage' },
  { value: 'collecting', label: 'Waiting on collection' },
  { value: 'ready', label: 'Ready to reconcile' },
  { value: 'reconciling', label: 'Reconciling now' },
  { value: 'fixes_back', label: 'Fixes in — re-checking' },
  { value: 'fix', label: 'Needs a fix (any side)' },
  { value: 'needs_client_fix', label: '— Client to fix' },
  { value: 'needs_our_fix', label: '— We need to fix' },
  { value: 'needs_both_fix', label: '— Both sides to fix' },
  { value: 'clean', label: 'Reconciled clean' },
  { value: 'failed', label: 'Run failed' },
]

const matchesStage = (stage, filter) =>
  filter === 'all' || (filter === 'fix' ? NEEDS_FIX.has(stage) : stage === filter)

/** `YYYY-MM` keys for every month we were chasing this cycle — from the
 *  day it opened to the day their statement arrived (or today). Named and
 *  filtered by these, not by the ledger's own period: a request opened in
 *  September for an "FY to Aug" ledger is September's chase, not April's. */
function monthsCovered(from, to) {
  const out = []
  let [y, m] = from.slice(0, 7).split('-').map(Number)
  const [ey, em] = to.slice(0, 7).split('-').map(Number)
  while (y < ey || (y === ey && m <= em)) {
    out.push(`${y}-${String(m).padStart(2, '0')}`)
    m += 1
    if (m > 12) {
      m = 1
      y += 1
    }
  }
  return out
}

const monthLabel = (key) => {
  const [y, m] = key.split('-').map(Number)
  return new Date(Date.UTC(y, m - 1, 1)).toLocaleDateString('en-IN', { month: 'long', year: 'numeric', timeZone: 'UTC' })
}

const COLLECTION_TEXT = {
  NOT_REQUESTED: 'Not asked yet',
  REQUESTED: 'Asked — waiting on them',
  RECEIVED: 'Their statement is in',
  READY: 'Both sides in',
}

const WAITING_TEXT = {
  statement: "Waiting for the client's statement",
  confirmation: "Their statement needs confirming on Ledger Collector",
  our_side: 'Our side not pulled from Gati yet',
}

const SIDE_TEXT = {
  needs_client_fix: 'Differences are on the client’s side',
  needs_our_fix: 'Differences are on our side — see Ledger Remediation',
  needs_both_fix: 'Differences on both sides',
  clean: 'Both ledgers agree',
}


function StagePill({ stage }) {
  const s = STAGES[stage] ?? STAGES.collecting
  return (
    <span className={`pill ${s.tone}`}>
      <span className={`d${stage === 'reconciling' ? ' rt-pulse' : ''}`} />
      {s.label}
    </span>
  )
}

/** What a fix cycle is still waiting on — the orchestrator's own reading of it. */
function fixDetail(row) {
  const ours = row.openFixes ? `${row.openFixes} of our fix${row.openFixes === 1 ? '' : 'es'} open` : null
  switch (row.waitingOn) {
    case 'client_refused':
      return 'They refused to correct it — post-reco chase stopped; needs a person to call them'
    case 'needs_person':
      return `Asked ${row.postRecoAsks || 7} times, no ledger back — needs a person: call them, then upload their statement from the ledgers drawer`
    case 'client_promised':
      return `They promised it by ${dateText(row.promisedUntil)} — asking again after that if it hasn’t come`
    case 'client_resend':
      return `Waiting for their corrected statement — asked ${row.postRecoAsks || 0} of 7 times`
    case 'client_confirmation':
      return 'Corrected statement in — confirm it on Ledger Collector'
    case 'our_fixes':
      return `${ours ?? 'Our fixes open'} — see Ledger Remediation`
    case 'fixes_not_raised':
      return 'Raising our fixes on the next pass'
    case 'both':
      return `Their corrected statement${ours ? ` and ${ours}` : ''}`
    default:
      return null
  }
}

function stageDetail(row) {
  if (row.stage === 'fixes_back') return 'Every side’s fix is in — our side is re-pulled and re-run on the next pass'
  if (['needs_client_fix', 'needs_our_fix', 'needs_both_fix'].includes(row.stage)) return fixDetail(row) ?? SIDE_TEXT[row.stage] ?? null
  if (row.stage === 'collecting') return WAITING_TEXT[row.waitingOn] ?? null
  if (row.stage === 'ready') return 'Both ledgers in and confirmed'
  if (row.stage === 'reconciling') return `Started ${whenText(row.pendingJob?.startedAt)}`
  if (row.stage === 'failed') return row.latestRun?.errorMessage || row.latestRun?.failureReason || 'No detail recorded'
  return SIDE_TEXT[row.stage] ?? null
}

const ROW_COLS = 'minmax(170px,1.2fr) 150px 150px minmax(240px,1.6fr) 110px'

function TrackerRow({ row, onOpen }) {
  const run = row.latestRun
  return (
    <div
      className="rowgrid"
      style={{ gridTemplateColumns: ROW_COLS }}
      role="button"
      tabIndex={0}
      title="Open this client's ledgers"
      onClick={onOpen}
      onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && (e.preventDefault(), onOpen())}
    >
      <span>
        <span className="c-name">{row.clientName || row.clientCode}</span>
        <span className="c-sub">
          {row.clientCode}
          <SampleTag show={isSampleCode(row.clientCode)} />
        </span>
      </span>
      <span>
        {/* What we ask for: from the period start till date (or till the
            day their statement came in). Under it, what they actually sent,
            or how long we've been asking. */}
        <span className="rt-period">{askRange(row.periodFrom, row.cycleTo)}</span>
        <span className="c-sub">
          {row.collectionStatus === 'RECEIVED' || row.collectionStatus === 'READY'
            ? row.coversFrom && row.coversTo
              ? `Sent ${monthRange(row.coversFrom, row.coversTo)}`
              : 'Sent — months not readable'
            : `Chasing since ${dateText(row.cycleFrom)}`}
        </span>
      </span>
      <span className="rt-coll">{COLLECTION_TEXT[row.collectionStatus] ?? row.collectionStatus}</span>
      <span className="rt-stage">
        <StagePill stage={row.stage} />
        <span className={`rt-detail${['needs_person', 'client_refused'].includes(row.waitingOn) ? ' rt-person' : ''}`} title={stageDetail(row) ?? undefined}>{stageDetail(row)}</span>
      </span>
      <span className="c-sub" style={{ marginTop: 0 }}>{run ? whenText(run.createdAt) : '—'}</span>
    </div>
  )
}

export default function ReconciliationTracker() {
  const [rows, setRows] = useState(null)
  const [error, setError] = useState(null)
  const [month, setMonth] = useState('all')
  const [text, setText] = useState('')
  const [stage, setStage] = useState('all')
  const [ledgersFor, setLedgersFor] = useState(null)

  const load = useCallback(async () => {
    try {
      const out = await api.getReconciliationTracker()
      setRows(out.rows ?? [])
      setError(null)
    } catch (e) {
      setError(e.message)
      setRows([])
    }
  }, [])

  useEffect(() => {
    load()
  }, [load])

  const withMonths = useMemo(
    () => (rows ?? []).map((r) => ({ ...r, months: monthsCovered(r.cycleFrom, r.cycleTo) })),
    [rows]
  )

  const monthOptions = useMemo(() => {
    const keys = [...new Set(withMonths.flatMap((r) => r.months))].sort().reverse()
    return [{ value: 'all', label: 'All months' }, ...keys.map((k) => ({ value: k, label: monthLabel(k) }))]
  }, [withMonths])

  // Month and client narrow what the tiles count; stage doesn't, so the
  // tiles still show the whole picture for the month you're looking at.
  const inScope = useMemo(() => {
    const needle = text.trim().toLowerCase()
    return withMonths.filter((r) => {
      if (month !== 'all' && !r.months.includes(month)) return false
      if (needle && !`${r.clientName ?? ''} ${r.clientCode}`.toLowerCase().includes(needle)) return false
      return true
    })
  }, [withMonths, month, text])

  const visible = useMemo(() => inScope.filter((r) => matchesStage(r.stage, stage)), [inScope, stage])

  const counts = useMemo(() => {
    const out = {}
    for (const t of TILES) out[t.key] = inScope.filter((r) => t.match(r.stage)).length
    return out
  }, [inScope])

  const narrowed = month !== 'all' || text.trim() || stage !== 'all'

  return (
    <>
      <div className="stats rt-stats">
        {TILES.map((t) => {
          const active = stage === t.key
          return (
            <button
              key={t.key}
              type="button"
              className={`stat rt-tile${active ? ' on' : ''}`}
              onClick={() => setStage(active ? 'all' : t.key)}
              title={active ? 'Show every stage' : `Show only: ${t.label.toLowerCase()}`}
            >
              <div className="stat-accent" style={{ background: t.accent }} />
              <div className="stat-label">{t.label}</div>
              <div className="stat-value">{rows === null ? '—' : counts[t.key]}</div>
            </button>
          )
        })}
      </div>

      <div className="panel">
        <div className="panel-head rt-head">
          <div className="search-box rt-search">
            <input
              type="text"
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Search client name or code"
              aria-label="Search client name or code"
            />
          </div>
          <div className="rt-filters">
            <Select options={monthOptions} value={month} onChange={setMonth} title="Month we were chasing in" />
            <Select options={STAGE_FILTERS} value={stage} onChange={setStage} title="Reconciliation stage" />
            {narrowed && (
              <button
                type="button"
                className="btn btn-sm btn-ghost"
                onClick={() => {
                  setMonth('all')
                  setText('')
                  setStage('all')
                }}
              >
                Clear
              </button>
            )}
          </div>
        </div>

        {error && (
          <div className="panel-pad">
            <div className="note" style={{ marginTop: 0 }}>{error}</div>
          </div>
        )}

        <div className="tbl" style={{ border: 'none', borderRadius: 0 }}>
          <div className="thead" style={{ gridTemplateColumns: ROW_COLS }}>
            <span>Client</span>
            <span>Ledger asked for</span>
            <span>Collection</span>
            <span>Reconciliation</span>
            <span>Last run</span>
          </div>
          {rows === null && <SkeletonRow cols={ROW_COLS} cells={5} count={5} />}
          {rows !== null && visible.length === 0 && (
            <div className="empty">
              {rows.length === 0
                ? 'No collection cycles yet — they appear here once a period is opened on Ledger Collector.'
                : 'No clients match these filters.'}
            </div>
          )}
          {visible.map((row) => (
            <TrackerRow
              key={row.ledgerRequestId}
              row={row}
              onOpen={() =>
                setLedgersFor({
                  _id: row.ledgerRequestId,
                  clientCode: row.clientCode,
                  clientName: row.clientName,
                  periodLabel: row.periodLabel,
                  theirSide: { receivedAt: ['RECEIVED', 'READY'].includes(row.collectionStatus) ? row.receivedAt ?? row.cycleTo : null },
                  postReco: row.waitingOn === 'needs_person' ? { asks: row.postRecoAsks || 7, needsPerson: true } : null,
                })
              }
            />
          ))}
        </div>
        {rows !== null && visible.length > 0 && (
          <div className="rt-foot">
            Showing {visible.length} of {rows.length} collection cycle{rows.length === 1 ? '' : 's'}
          </div>
        )}
      </div>
      {ledgersFor && <ClientLedgers request={ledgersFor} onClose={() => setLedgersFor(null)} onChanged={load} />}
    </>
  )
}
